import { supabase } from "@/integrations/supabase/client";

export interface TailoredDocument {
  id: string;
  user_id: string;
  tailoring_session_id: string;
  document_type: 'resume' | 'cover_letter' | 'thank_you_letter' | 'executive_bio' | 'linkedin_profile' | 'outreach_letter';
  title: string;
  content: string;
  job_title?: string;
  company_name?: string;
  job_description?: string;
  match_score?: number;
  original_match_score?: number;
  analysis_results?: any;
  original_resume_id?: string;
  file_name?: string;
  file_type?: string;
  word_document_url?: string;
  word_document_path?: string;
  original_content?: string;
  parsed_document_data?: any;
  document_json_data?: any;
  processing_status: string;
  created_at: string;
  updated_at: string;
}

export interface TailoredDocumentSession {
  session_id: string;
  job_title: string;
  company_name: string;
  created_at: string;
  documents: TailoredDocument[];
  resume_document?: TailoredDocument;
  cover_letter_document?: TailoredDocument;
  thank_you_letter_document?: TailoredDocument;
  executive_bio_document?: TailoredDocument;
  linkedin_profile_document?: TailoredDocument;
  outreach_letter_document?: TailoredDocument;
  // User profile information
  user_name?: string;
  user_email?: string;
  user_display_name?: string;
}

export class TailoredDocumentService {
  
  /**
   * Safely extract string content from VectorShift results
   * VectorShift may return letters as objects with { Body: "...", Name: "...", ... }
   */
  private static safeString(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    // VectorShift letter objects have a Body field - extract it
    if (typeof value === 'object' && value.Body && typeof value.Body === 'string') {
      return value.Body;
    }
    // For other objects, try to stringify for preservation
    if (typeof value === 'object') {
      console.warn('safeString received object without Body field:', Object.keys(value));
      return JSON.stringify(value);
    }
    return String(value);
  }
  
  /**
   * Extract candidate name from resume content
   */
  private static extractCandidateName(content: string): string {
    if (!content) return 'Unknown Candidate';
    
    const trimmedContent = content.trim();
    
    // Try multiple patterns to find the candidate name at the start of the resume
    // Pattern 1: Name at very beginning (most common)
    const namePattern1 = trimmedContent.match(/^([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
    
    // Pattern 2: Name after some whitespace/formatting
    const namePattern2 = trimmedContent.match(/^\s*([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
    
    // Pattern 3: Look for name in first few lines
    const firstLines = trimmedContent.split('\n').slice(0, 5).join(' ');
    const namePattern3 = firstLines.match(/([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
    
    // Pattern 4: More flexible name pattern (handles middle initials, etc.)
    const namePattern4 = trimmedContent.match(/\b([A-Z][a-z]+(?:\s+[A-Z]\.?\s*)?[A-Z][a-z]+)\b/);
    
    return namePattern1?.[1] || namePattern2?.[1] || namePattern3?.[1] || namePattern4?.[1] || 'Unknown Candidate';
  }

  /**
   * Generate JSON data for a document based on its type and content
   */
  private static async generateDocumentJsonData(content: string, documentType: string, candidateName: string): Promise<any> {
    try {
      console.log(`🔄 Calling parse-document-to-json for ${documentType}...`);
      console.log(`📄 Content length: ${content?.length || 0}`);
      console.log(`📄 Content preview (first 100 chars): ${content?.substring(0, 100) || 'EMPTY/NULL'}`);
      
      // Call the parse-document-to-json edge function to generate structured JSON
      const { data, error } = await supabase.functions.invoke('parse-document-to-json', {
        body: {
          documentContent: content, // Fixed: use documentContent instead of content
          documentType: documentType,
          // Optional parameters - not needed for basic JSON generation
          documentId: null,
          sessionId: null,
          userId: null
        }
      });

      if (error) {
        console.error(`❌ Error generating JSON for ${documentType}:`, error);
        return null;
      }

      console.log(`✅ JSON generation response for ${documentType}:`, data?.success ? 'SUCCESS' : 'FAILED');
      if (data?.documentJson) {
        console.log(`📊 Generated JSON size for ${documentType}: ${JSON.stringify(data.documentJson).length} characters`);
      }
      return data?.documentJson || null; // Fixed: use documentJson instead of jsonData
    } catch (error) {
      console.error(`💥 Failed to generate JSON for ${documentType}:`, error);
      return null;
    }
  }

  /**
   * Save all documents from a tailoring session
   */
  static async saveTailoringSession(
    tailoringResults: any,
    jobTitle: string,
    companyName: string,
    jobDescription: string,
    selectedDocuments: string[] = ['resume'], // Add selectedDocuments parameter with default
    originalResumeId?: string,
    generatedFilename?: string, // Add generatedFilename parameter
    tailoringDurationSeconds?: number, // Add tailoring duration in seconds
    // Complete results-page state (tailoringResults incl. Resume_JSON, analysis
    // inputs, job info, build criteria). Stored on the RESUME row so the
    // library can reopen the full results screen later via ?sessionId=.
    resultsPageState?: any
  ): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Generate a unique session ID for this tailoring session 
    const sessionId = crypto.randomUUID();
    
    // Extract candidate name from resume content for proper naming
    const resumeContent = this.safeString(tailoringResults.Final_Resume_Text);
    const candidateName = this.extractCandidateName(resumeContent);
    
    console.log('Extracted candidate name:', candidateName);

    const documents: any[] = [];

    // Map VectorShift results to document types - only for selected documents
    const documentMapping = [
      { key: 'Final_Resume_Text', type: 'resume' as const, name: 'Resume', selector: 'resume' },
      { key: 'Cover_Letter', type: 'cover_letter' as const, name: 'Cover Letter', selector: 'cover-letter' },
      { key: 'Thank_You_Letter', type: 'thank_you_letter' as const, name: 'Thank You Letter', selector: 'thank-you-letter' },
      { key: 'Executive_Bio', type: 'executive_bio' as const, name: 'Executive Bio', selector: 'executive-bio' },
      { key: 'Linkedin_Profile', type: 'linkedin_profile' as const, name: 'LinkedIn Profile', selector: 'linkedin-profile' },
      { key: 'Outreach_Letter', type: 'outreach_letter' as const, name: 'Outreach Letter', selector: 'outreach-email' }
    ];

    // Create document records ONLY for documents the user selected
    for (const mapping of documentMapping) {
      // Safely extract string content - VectorShift may return objects with Body field.
      // Executive bios now arrive as structured JSON (the navy one-page design);
      // persist the structure verbatim so the results page can re-render it.
      const rawContent = mapping.type === 'executive_bio'
        ? (tailoringResults['Executive_Bio_JSON'] ?? tailoringResults[mapping.key])
        : tailoringResults[mapping.key];
      const content = (rawContent && typeof rawContent === 'object')
        ? JSON.stringify(rawContent)
        : this.safeString(rawContent);
      
      // Only save if user selected this document type AND content exists
      if (selectedDocuments.includes(mapping.selector) && content && content.trim()) {
        console.log(`Generating JSON data for ${mapping.type}...`);
        
        // Generate JSON data for this document
        const jsonData = await this.generateDocumentJsonData(content.trim(), mapping.type, candidateName);
        console.log(`JSON generation result for ${mapping.type}:`, jsonData ? 'SUCCESS' : 'FAILED');
        console.log(`JSON data size for ${mapping.type}:`, jsonData ? JSON.stringify(jsonData).length : 0);
        
        // Use the passed filename or generate fallback
        const baseFileName = generatedFilename ? 
          generatedFilename.replace('.docx', '') : 
          `${candidateName} - ${jobTitle || 'Position'} - ${companyName || 'Company'} - ${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
        
        const docTypeLabel = mapping.type === 'cover_letter' ? 'Cover Letter' : 
                            mapping.type === 'thank_you_letter' ? 'Thank You Letter' :
                            mapping.type === 'executive_bio' ? 'Executive Bio' :
                            mapping.type === 'linkedin_profile' ? 'LinkedIn Profile' :
                            mapping.type === 'outreach_letter' ? 'Outreach Letter' : 'Resume';
        
        const documentFileName = mapping.type === 'resume' ? baseFileName : `${baseFileName.replace(' - Resume', '')} - ${docTypeLabel}`;
        
        documents.push({
          user_id: user.id,
          tailoring_session_id: sessionId,
          document_type: mapping.type,
          title: documentFileName,
          content: content.trim(),
          job_title: jobTitle,
          company_name: companyName,
          job_description: jobDescription,
          match_score: mapping.type === 'resume' ? tailoringResults.Tailored_Match_Score : null,
          original_match_score: mapping.type === 'resume' ? tailoringResults.Original_Match_Score : null,
          analysis_results: mapping.type === 'resume' ? (() => {
            try {
              // Sanitize and validate the analysis results before saving
              const analysisResults = {
                original_analysis: tailoringResults.original_analysis,
                tailored_analysis: tailoringResults.tailored_analysis,
                changes_analysis: tailoringResults.changes_analysis,
                improvement_report: tailoringResults.improvement_report,
                // Store tailoring duration in metadata for retrieval after page refresh
                metadata: {
                  tailoring_duration_seconds: tailoringDurationSeconds || null,
                  saved_at: new Date().toISOString()
                },
                // Full state needed to reopen the results page from the library.
                _results_page: resultsPageState ?? null
              };
              
              // Ensure all fields are valid JSON or null
              Object.keys(analysisResults).forEach(key => {
                if (key === 'metadata') return; // Skip metadata validation
                const value = analysisResults[key];
                if (value !== null && value !== undefined) {
                  // If it's a string, try to parse it as JSON to validate
                  if (typeof value === 'string') {
                    try {
                      JSON.parse(value);
                    } catch (e) {
                      console.warn(`Invalid JSON in ${key}, setting to null:`, e.message);
                      analysisResults[key] = null;
                    }
                  }
                  // If it's an object, ensure it can be stringified
                  else if (typeof value === 'object') {
                    try {
                      JSON.stringify(value);
                    } catch (e) {
                      console.warn(`Cannot stringify ${key}, setting to null:`, e.message);
                      analysisResults[key] = null;
                    }
                  }
                }
              });
              
              return analysisResults;
            } catch (e) {
              console.error('Error processing analysis results:', e);
              return null;
            }
          })() : null,
          original_resume_id: originalResumeId,
          file_name: `${documentFileName}.docx`,
          file_type: 'text/plain',
          document_json_data: jsonData, // Store the generated JSON data
          processing_status: 'completed',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
        console.log(`Generated JSON data for ${mapping.type}:`, jsonData ? 'Success' : 'Failed');
      }
    }

    if (documents.length === 0) {
      throw new Error('No valid documents found in tailoring results');
    }

    // Insert all documents in a single batch
    const { error } = await supabase
      .from('tailored_documents')
      .insert(documents);

    if (error) {
      console.error('Error saving tailored documents:', error);
      throw error;
    }

    console.log(`Successfully saved ${documents.length} documents for tailoring session:`, sessionId);
    return sessionId;
  }

  /**
   * Calculate content similarity between two strings (simple word-based comparison)
   */
  private static calculateContentSimilarity(content1: string, content2: string): number {
    const words1 = new Set(content1.toLowerCase().split(/\s+/).filter(w => w.length > 3));
    const words2 = new Set(content2.toLowerCase().split(/\s+/).filter(w => w.length > 3));
    
    const intersection = new Set([...words1].filter(w => words2.has(w)));
    const union = new Set([...words1, ...words2]);
    
    return union.size > 0 ? intersection.size / union.size : 0;
  }

  /**
   * Get all tailored document sessions for the current user
   * Includes both new format (tailored_documents table) and legacy format (resumes table)
   */
  static async getTailoredDocumentSessions(): Promise<TailoredDocumentSession[]> {
    // Hard timeout on auth.getUser so this can never hang forever (observed in
    // the wild: a stale/unrefreshable token can make getUser() pend without
    // ever resolving, which leaves the library page spinning indefinitely).
    const { data: authData } = await Promise.race([
      supabase.auth.getUser(),
      new Promise<{ data: { user: null } }>((resolve) =>
        setTimeout(() => resolve({ data: { user: null } }), 8000),
      ),
    ]) as { data: { user: { id: string } | null } };
    const user = authData?.user;

    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('=== FETCHING TAILORED DOCUMENT SESSIONS WITH PROFILE DATA ===');
    console.log('Current user ID:', user.id);

    // Run the three queries in parallel; profiles is tolerated as best-effort
    // since it isn't required to render the library. Using maybeSingle()
    // instead of single() so a missing profile row returns null (data, null
    // error) instead of throwing a 406 we have to swallow.
    const [newQuery, profileQuery, legacyQuery] = await Promise.all([
      supabase
        .from('tailored_documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('profiles')
        .select('display_name, email, first_name, last_name')
        .eq('id', user.id)
        .maybeSingle(),
      supabase
        .from('resumes')
        .select('*')
        .eq('user_id', user.id)
        .not('original_match_score', 'is', null)
        .gt('original_match_score', 0) // Exclude "before" versions
        .order('created_at', { ascending: false }),
    ]);

    const { data: newDocuments, error: newError } = newQuery;
    const { data: profileData, error: profileError } = profileQuery;
    const { data: legacyResumes, error: legacyError } = legacyQuery;

    if (profileError) {
      console.warn('Profile fetch failed (non-blocking):', profileError);
    }

    if (newError) {
      console.error('Error fetching new tailored documents:', newError);
      throw newError;
    }

    if (legacyError) {
      console.error('Error fetching legacy tailored resumes:', legacyError);
      throw legacyError;
    }

    const sessions: TailoredDocumentSession[] = [];

    // Process new format documents (grouped by session)
    if (newDocuments && newDocuments.length > 0) {
      const sessionMap = new Map<string, TailoredDocument[]>();
      
      newDocuments.forEach(doc => {
        if (!sessionMap.has(doc.tailoring_session_id)) {
          sessionMap.set(doc.tailoring_session_id, []);
        }
        sessionMap.get(doc.tailoring_session_id)!.push(doc as TailoredDocument);
      });

      for (const [sessionId, documents] of sessionMap.entries()) {
        const sortedDocs = documents.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const resumeDoc = sortedDocs.find(d => d.document_type === 'resume');
        
        // Extract candidate name directly from resume content
        console.log('Resume document data:', resumeDoc);
        console.log('Content preview:', resumeDoc?.content?.substring(0, 300));
        
        let candidateName = 'Unknown Candidate';
        
        if (resumeDoc?.content) {
          // Try multiple patterns to find the candidate name at the start of the resume
          const content = resumeDoc.content.trim();
          
          // Pattern 1: Name at very beginning (most common)
          const namePattern1 = content.match(/^([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
          
          // Pattern 2: Name after some whitespace/formatting
          const namePattern2 = content.match(/^\s*([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
          
          // Pattern 3: Look for name in first few lines
          const firstLines = content.split('\n').slice(0, 5).join(' ');
          const namePattern3 = firstLines.match(/([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
          
          // Pattern 4: More flexible name pattern (handles middle initials, etc.)
          const namePattern4 = content.match(/\b([A-Z][a-z]+(?:\s+[A-Z]\.?\s*)?[A-Z][a-z]+)\b/);
          
          candidateName = namePattern1?.[1] || namePattern2?.[1] || namePattern3?.[1] || namePattern4?.[1] || candidateName;
        }
        
        // Also try to get from parsed data if available
        if (candidateName === 'Unknown Candidate' && resumeDoc?.parsed_document_data) {
          const resumeData = resumeDoc.parsed_document_data as any;
          candidateName = resumeData?.personalInformation?.name ||
                         resumeData?.personal_information?.name ||
                         resumeData?.personalInfo?.name ||
                         resumeData?.contact?.name ||
                         resumeData?.name ||
                         candidateName;
        }
        
        console.log('Final extracted candidate name:', candidateName);
        const userName = candidateName;
        
        const session: TailoredDocumentSession = {
          session_id: sessionId,
          job_title: resumeDoc?.job_title || 'Unknown Position',
          company_name: resumeDoc?.company_name || 'Unknown Company',
          created_at: sortedDocs[0]?.created_at || new Date().toISOString(),
          documents: sortedDocs,
          resume_document: documents.find(d => d.document_type === 'resume'),
          cover_letter_document: documents.find(d => d.document_type === 'cover_letter'),
          thank_you_letter_document: documents.find(d => d.document_type === 'thank_you_letter'),
          executive_bio_document: documents.find(d => d.document_type === 'executive_bio'),
          linkedin_profile_document: documents.find(d => d.document_type === 'linkedin_profile'),
          outreach_letter_document: documents.find(d => d.document_type === 'outreach_letter'),
          user_name: userName,
          user_email: profileData?.email,
          user_display_name: profileData?.display_name
        };
        
        sessions.push(session);
      }
    }

    // Process legacy format documents (each resume is its own session)
    if (legacyResumes && legacyResumes.length > 0) {
      for (const resume of legacyResumes) {
        // Extract candidate name from legacy resume content
        console.log('Legacy resume data:', resume);
        console.log('Legacy content preview:', resume.content?.substring(0, 300));
        
        let candidateName = 'Unknown Candidate';
        
        if (resume.content) {
          // Try multiple patterns to find the candidate name at the start of the resume
          const content = resume.content.trim();
          
          // Pattern 1: Name at very beginning (most common)
          const namePattern1 = content.match(/^([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
          
          // Pattern 2: Name after some whitespace/formatting
          const namePattern2 = content.match(/^\s*([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
          
          // Pattern 3: Look for name in first few lines
          const firstLines = content.split('\n').slice(0, 5).join(' ');
          const namePattern3 = firstLines.match(/([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)/);
          
          // Pattern 4: More flexible name pattern (handles middle initials, etc.)
          const namePattern4 = content.match(/\b([A-Z][a-z]+(?:\s+[A-Z]\.?\s*)?[A-Z][a-z]+)\b/);
          
          candidateName = namePattern1?.[1] || namePattern2?.[1] || namePattern3?.[1] || namePattern4?.[1] || candidateName;
        }
        
        // Also try to get from parsed data if available
        if (candidateName === 'Unknown Candidate' && resume.parsed_resume_data) {
          const resumeData = resume.parsed_resume_data as any;
          candidateName = resumeData?.personal_information?.name ||
                         resumeData?.personalInfo?.name ||
                         resumeData?.name ||
                         resumeData?.personalInformation?.name ||
                         candidateName;
        }
        
        console.log('Legacy extracted candidate name:', candidateName);
        const userName = candidateName;
        
        // Convert legacy resume to new document format for display
        const legacyDocument: TailoredDocument = {
          id: resume.id,
          user_id: resume.user_id,
          tailoring_session_id: resume.id, // Use resume ID as session ID for legacy
          document_type: 'resume' as const,
          title: resume.title,
          content: resume.content || '',
          job_title: resume.job_title,
          company_name: resume.company_name,
          job_description: resume.job_description,
          match_score: resume.match_score,
          original_match_score: resume.original_match_score,
          analysis_results: resume.analysis_results,
          original_resume_id: resume.original_resume_id,
          file_name: resume.file_name,
          file_type: resume.file_type,
          word_document_url: resume.word_document_url,
          word_document_path: resume.word_document_path,
          original_content: resume.original_content,
          parsed_document_data: resume.parsed_resume_data,
          processing_status: resume.processing_status || 'completed',
          created_at: resume.created_at,
          updated_at: resume.updated_at
        };

        const legacySession: TailoredDocumentSession = {
          session_id: resume.id,
          job_title: resume.job_title || 'Unknown Position',
          company_name: resume.company_name || 'Unknown Company',
          created_at: resume.created_at,
          documents: [legacyDocument],
          resume_document: legacyDocument,
          cover_letter_document: undefined,
          thank_you_letter_document: undefined,
          executive_bio_document: undefined,
          linkedin_profile_document: undefined,
          outreach_letter_document: undefined,
          user_name: userName,
          user_email: profileData?.email,
          user_display_name: profileData?.display_name
        };

        sessions.push(legacySession);
      }
    }

    return sessions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  /**
   * Get a specific tailored document session by ID
   */
  static async getTailoredDocumentSession(sessionId: string): Promise<TailoredDocumentSession | null> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('tailored_documents')
      .select('*')
      .eq('user_id', user.id)
      .eq('tailoring_session_id', sessionId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tailored document session:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return null;
    }

    const documents = data as TailoredDocument[];
    const resumeDoc = documents.find(d => d.document_type === 'resume');

    return {
      session_id: sessionId,
      job_title: resumeDoc?.job_title || 'Unknown Position',
      company_name: resumeDoc?.company_name || 'Unknown Company',
      created_at: documents[0]?.created_at || new Date().toISOString(),
      documents: documents,
      resume_document: documents.find(d => d.document_type === 'resume'),
      cover_letter_document: documents.find(d => d.document_type === 'cover_letter'),
      thank_you_letter_document: documents.find(d => d.document_type === 'thank_you_letter'),
      executive_bio_document: documents.find(d => d.document_type === 'executive_bio'),
      linkedin_profile_document: documents.find(d => d.document_type === 'linkedin_profile'),
      outreach_letter_document: documents.find(d => d.document_type === 'outreach_letter')
    };
  }

  /**
   * Delete a tailored document session (all documents in the session)
   * Handles both new format and legacy format deletions
   */
  static async deleteTailoredDocumentSession(sessionId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // First try to delete from new tailored_documents table
    const { error: newError } = await supabase
      .from('tailored_documents')
      .delete()
      .eq('user_id', user.id) 
      .eq('tailoring_session_id', sessionId);

    if (newError) {
      console.error('Error deleting from tailored_documents:', newError);
    }

    // Also try to delete from legacy resumes table (for backward compatibility)
    // sessionId equals the resume.id for legacy documents
    const { error: legacyError } = await supabase
      .from('resumes')
      .delete()
      .eq('user_id', user.id)
      .eq('id', sessionId);

    if (legacyError) {
      console.error('Error deleting from legacy resumes:', legacyError);
    }

    // If both operations had errors, throw the new table error
    if (newError && legacyError) {
      throw newError;
    }
  }

  /**
   * Delete a specific document from a session
   */
  static async deleteTailoredDocument(documentId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('tailored_documents')
      .delete()
      .eq('user_id', user.id)
      .eq('id', documentId);

    if (error) {
      console.error('Error deleting tailored document:', error);
      throw error;
    }
  }

}